import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getNotifications, markNotificationAsRead, markAllNotificationsAsRead, subscribeToNotifications, dismissNotification } from '../utils/notifications';
import { isAuthenticated, getCachedUser } from '../utils/auth';
import NotificationLottieIcon from './NotificationLottieIcon';
import { playBoostingRequestSound, playNotificationSound } from '../utils/notificationSound';
import '../styles/NotificationDropdown.css';

// Separate component for notification item to manage hover state
const NotificationItem = ({ notification, onMarkAsRead, formatTime, onNavigate, onDismiss }) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  
  const handleClick = async () => {
    try {
    if (!notification.read) {
      onMarkAsRead(notification.id);
    }
    
    // Navigate based on notification type
      // Boosting notices always stay on the request page — never /orders/:id
      if (notification.metadata?.boosting_request_id) {
        navigate(`/boosting/requests/${notification.metadata.boosting_request_id}`);
      } else if (notification.metadata?.conversation_id) {
        navigate(`/profile/messages?conversation=${notification.metadata.conversation_id}`);
    } else if (notification.metadata?.order_id) {
      navigate(`/orders/${notification.metadata.order_id}`);
    }
    
    if (onNavigate) {
      onNavigate(notification);
      }
    } catch (error) {
      console.error('Error navigating from notification:', error);
      // Still try to navigate even if there's an error
      if (notification.metadata?.order_id) {
        navigate(`/orders/${notification.metadata.order_id}`);
      }
    }
  };
  
  return (
    <div
      className={`notification-item ${!notification.read ? 'unread' : ''}`}
      data-type={notification.type}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ cursor: 'pointer' }}
    >
      <div className="notification-icon">
        <NotificationLottieIcon notification={notification} size={32} isParentHovered={isHovered} />
      </div>
      <div className="notification-content">
        <div className="notification-title">{notification.title}</div>
        <div className="notification-message">
          {notification.message}
          {notification.metadata?.price_display && (
            <div className="notification-price">{notification.metadata.price_display}</div>
          )}
        </div>
        <div className="notification-time">{formatTime(notification.created_at)}</div>
      </div>
      <button
        className="notification-close-btn"
        onClick={(e) => onDismiss(notification.id, e)}
        title="Dismiss"
      >
        ✕
      </button>
      {!notification.read && <div className="notification-dot"></div>}
    </div>
  );
};

const NotificationDropdown = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated()) {
      setLoading(false);
      return;
    }

    const user = getCachedUser();
    if (!user) {
      setLoading(false);
      return;
    }

    // Load initial notifications
    const loadNotifications = async () => {
      const currentUser = getCachedUser();
      if (!currentUser) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const { data } = await getNotifications(currentUser.id);
      if (data) {
        setNotifications(data);
        const unread = data.filter(n => !n.read).length;
        setUnreadCount(unread);
      }
      setLoading(false);
    };

    loadNotifications();

    // Subscribe to real-time notifications
    const unsubscribe = subscribeToNotifications(user.id, (newNotification) => {
      console.log('New notification received:', newNotification);
      setNotifications(prev => [newNotification, ...prev]);
      setUnreadCount(prev => prev + 1);
      
      // Play sound notification for boosting requests
      if (newNotification.type === 'new_boosting_request') {
        playBoostingRequestSound();
      } else {
        // Play regular notification sound for other notifications
        playNotificationSound();
      }
      
      // Show browser notification if permission granted
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(newNotification.title, {
          body: newNotification.message,
          icon: '/logo192.png',
        });
      }
    });

    // Reload notifications when user logs in
    const handleUserLogin = () => {
      const currentUser = getCachedUser();
      if (currentUser) {
        loadNotifications();
      }
    };

    window.addEventListener('user-login', handleUserLogin);

    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      unsubscribe();
      window.removeEventListener('user-login', handleUserLogin);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close notifications when profile menu opens
  useEffect(() => {
    const handleUserMenuOpened = () => {
      setIsOpen(false);
    };

    window.addEventListener('user-menu-opened', handleUserMenuOpened);
    return () => {
      window.removeEventListener('user-menu-opened', handleUserMenuOpened);
    };
  }, []);

  const handleMarkAsRead = async (notificationId) => {
    await markNotificationAsRead(notificationId);
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const handleDismiss = async (notificationId, e) => {
    e.stopPropagation();
    await dismissNotification(notificationId);
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    // Update unread count if it was unread
    const notification = notifications.find(n => n.id === notificationId);
    if (notification && !notification.read) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  const handleMarkAllAsRead = async () => {
    const user = getCachedUser();
    if (!user) return;
    
    await markAllNotificationsAsRead(user.id);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };


  if (!isAuthenticated()) {
    return null;
  }

  return (
    <div className="notification-dropdown-wrapper" ref={dropdownRef}>
      <button
        className="notification-icon-btn"
        onClick={() => {
          const nextOpen = !isOpen;
          setIsOpen(nextOpen);
          if (nextOpen) {
            window.dispatchEvent(new CustomEvent('notifications-dropdown-opened'));
          }
        }}
        title="Notifications"
      >
        <span
          className="user-menu-header-icon"
          style={{
            WebkitMaskImage: 'url(/main/dashboardicons/umi/bell-umi.svg)',
            maskImage: 'url(/main/dashboardicons/umi/bell-umi.svg)',
          }}
          aria-hidden
        />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button
                className="mark-all-read-btn"
                onClick={handleMarkAllAsRead}
              >
                Mark all as read
              </button>
            )}
          </div>

          <div className="notification-list">
            {loading ? (
              <div className="notification-list">
                {[1, 2, 3].map(i => (
                  <div key={i} className="notification-item-skeleton">
                    <div className="skeleton skeleton-notification-icon" style={{ width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <div className="skeleton" style={{ width: '150px', height: '14px' }} />
                        <div className="skeleton" style={{ width: '60px', height: '12px' }} />
                      </div>
                      <div className="skeleton" style={{ width: '80%', height: '12px', marginBottom: '0.25rem' }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">No notifications</div>
            ) : (
              notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={handleMarkAsRead}
                  formatTime={formatTime}
                  onNavigate={(notif) => {
                    setIsOpen(false);
                  }}
                  onDismiss={handleDismiss}
                />
              ))
            )}
          </div>

          {notifications.length > 0 && (
            <div className="notification-footer">
              <Link to="/profile/notifications" onClick={() => setIsOpen(false)}>
                View all notifications
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;

